import { useQuery } from '@tanstack/react-query';
import { useNavigate, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { me } from '@/lib/auth';
import { TenantPurgeModal } from './TenantPurgeModal';
import { SendTestReportModal } from './SendTestReportModal';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card } from '@/components/Card';
import { CardHeader } from '@/components/CardHeader';
import { KPICard } from '@/components/KPICard';
import { RatioBar } from '@/components/RatioBar';
import { SevBadge } from '@/components/SevBadge';
import { EmptyState } from '@/components/EmptyState';
import { Check, Download, FileText, Globe, Mail, Plus, X } from 'lucide-react';
import { formatNumber, formatDate, formatPercent, formatRelativeTime } from '@/lib/locale';
import { apiGet, lastNDays, qs } from '@/features/_shared/queries';
import { QueryState } from '@/features/_shared/QueryState';

type AnalyticsOverview = {
  total_messages: number;
  pass_rate: number;
  spf_pass_rate?: number;
  dkim_pass_rate?: number;
  delta_messages_pct?: number;
  delta_pass_pp?: number;
};

type Tenant = {
  id: string;
  slug: string;
  name: string;
  // Lifecycle status from the backend (TenantOut.status): active | suspended |
  // archived. NOT a health rollup — earlier this was mistyped as a health enum,
  // so the live "active" value missed the badge map and crashed the page.
  status?: 'active' | 'suspended' | 'archived';
  ingest_address?: string;
  monitored_since?: string;
  domains_count?: number;
  volume_7d?: number;
};

type TimelinePoint = {
  ts: string;
  pass: number;
  quarantine: number;
  reject: number;
};

type SourceRow = {
  source_ip: string;
  asn?: number | null;
  asn_org?: string | null;
  country?: string | null;
  reverse_dns?: string | null;
  esp_name?: string | null;
  esp_category?: string | null;
  esp_legitimacy?: string | null;
  count?: number | null;
  pass_count?: number;
  fail_count?: number;
  pass_ratio?: number | null;
};

type Delivery = {
  id: string;
  type: 'weekly' | 'monthly' | 'alert' | 'onboarding' | 'test';
  period?: string;
  pages?: number;
  size_bytes?: number;
  recipients_count?: number;
  smtp_status?: 'queued' | 'sent' | 'bounced' | 'failed';
  sent_at?: string;
};

type AlertEvent = {
  id: string;
  severity: 'critical' | 'warning' | 'info';
  summary: string;
  triggered_at: string;
  acknowledged_at?: string | null;
};

export function TenantDashboard({ slug }: { slug: string }) {
  const range = useMemo(() => lastNDays(30), []);
  const navigate = useNavigate();
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const meQ = useQuery({ queryKey: ['auth', 'me'], queryFn: () => me() });
  const isAdmin = meQ.data?.platform_role === 'admin';

  const tenantQ = useQuery({
    queryKey: ['tenants', slug],
    queryFn: () => apiGet<Tenant>(`/tenants/${slug}`, { tenant: slug }),
  });
  const overviewQ = useQuery({
    queryKey: ['analytics', 'overview', slug],
    queryFn: () =>
      apiGet<AnalyticsOverview>(`/analytics/overview${qs({ tenant: slug })}`, {
        tenant: slug,
      }),
  });
  const timelineQ = useQuery({
    queryKey: ['analytics', 'timeline', slug, range.from, range.to],
    queryFn: () =>
      apiGet<{ points: TimelinePoint[] }>(
        `/analytics/timeline${qs({
          tenant: slug,
          granularity: 'day',
          since: range.from,
          until: range.to,
        })}`,
        { tenant: slug },
      ),
  });
  const sourcesQ = useQuery({
    queryKey: ['analytics', 'sources', slug],
    queryFn: () =>
      apiGet<SourceRow[]>(`/analytics/sources${qs({ tenant: slug, per_page: 10 })}`, {
        tenant: slug,
      }),
  });
  const deliveriesQ = useQuery({
    queryKey: ['deliveries', slug, 'recent'],
    queryFn: () =>
      apiGet<Delivery[]>(`/tenants/${slug}/deliveries${qs({ page: 1, per_page: 5 })}`, {
        tenant: slug,
      }),
  });
  const alertsQ = useQuery({
    queryKey: ['alerts', 'recent', slug],
    queryFn: () =>
      apiGet<AlertEvent[]>(`/alert-events${qs({ tenant: slug, page: 1, per_page: 5 })}`, {
        tenant: slug,
      }),
  });

  const tenant = tenantQ.data;
  const overview = overviewQ.data;
  const timeline = timelineQ.data?.points ?? [];
  const sources = sourcesQ.data ?? [];
  const deliveries = deliveriesQ.data ?? [];
  const alerts = alertsQ.data ?? [];

  const isLoading = tenantQ.isLoading || overviewQ.isLoading;
  const isError = tenantQ.isError || overviewQ.isError;
  const onRetry = () => {
    tenantQ.refetch();
    overviewQ.refetch();
    timelineQ.refetch();
    sourcesQ.refetch();
    deliveriesQ.refetch();
    alertsQ.refetch();
  };

  return (
    <div className="max-w-[1600px] space-y-5 p-5">
      <QueryState isLoading={isLoading} isError={isError} onRetry={onRetry}>
        {/* Tenant header */}
        <Card className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-[24px] font-bold tracking-tight text-slate-900">
                  {tenant?.name ?? slug}
                </h1>
                <StatusBadge status={tenant?.status} />
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-4 text-[12px] text-slate-500">
                <span className="inline-flex items-center gap-1.5">
                  <Globe size={12} />
                  {formatNumber(tenant?.domains_count ?? 0)} gemonitorde domeinen
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Mail size={12} />
                  {formatNumber(overview?.total_messages ?? 0)} berichten / 30d
                </span>
                {tenant?.monitored_since && <span>Sinds {formatDate(tenant.monitored_since)}</span>}
                <span className="text-slate-400">·</span>
                <span className="font-mono">slug={slug}</span>
              </div>
              {tenant?.ingest_address && (
                <div className="mt-2 inline-flex items-center gap-2 rounded border border-slate-200 bg-slate-50 px-2.5 py-1 text-[12px]">
                  <Mail size={12} className="text-slate-400" />
                  <span className="text-slate-500">DMARC-reports (RUA) naar:</span>
                  <span className="select-all font-mono text-slate-800">{tenant.ingest_address}</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/tenants/$slug/recipients"
                params={{ slug }}
                className="inline-flex items-center gap-1.5 rounded border border-slate-200 px-3 py-1.5 text-[12px] text-slate-700 hover:bg-slate-50"
              >
                <Mail size={12} />
                Contacten
              </Link>
              <Link
                to="/tenants/$slug/deliveries"
                params={{ slug }}
                className="inline-flex items-center gap-1.5 rounded border border-slate-200 px-3 py-1.5 text-[12px] text-slate-700 hover:bg-slate-50"
              >
                <Download size={12} />
                Verzendingen
              </Link>
              <button
                type="button"
                onClick={() => setReportOpen(true)}
                className="inline-flex items-center gap-1.5 rounded bg-brand-600 px-3 py-1.5 text-[12px] font-medium text-slate-900 hover:bg-brand-500"
              >
                <FileText size={12} />
                Rapport genereren
              </button>
            </div>
          </div>
        </Card>

        {/* KPI row */}
        <div className="grid grid-cols-4 gap-3">
          <KPICard
            label="Totaal berichten · 30d"
            value={formatNumber(overview?.total_messages ?? 0)}
            delta={fmtDelta(overview?.delta_messages_pct, '%', 'pct')}
            deltaTone={toneOf(overview?.delta_messages_pct)}
            sub="30 dagen rolling"
          />
          <KPICard
            label="DMARC pass"
            value={overview ? formatPercent(overview.pass_rate) : '—'}
            accent={accentForPass(overview?.pass_rate)}
            delta={fmtDelta(overview?.delta_pass_pp, ' pp', 'pp')}
            deltaTone={toneOf(overview?.delta_pass_pp)}
            sub="SLA-drempel 95 %"
          />
          <Link to="/tenants/$slug/domains" params={{ slug }} className="block">
            <KPICard
              label="SPF pass"
              value={overview?.spf_pass_rate != null ? formatPercent(overview.spf_pass_rate) : '—'}
              sub="per domein → domeinenoverzicht"
              className="h-full transition hover:border-brand-300 hover:shadow-sm"
            />
          </Link>
          <Link to="/tenants/$slug/domains" params={{ slug }} className="block">
            <KPICard
              label="DKIM pass"
              value={overview?.dkim_pass_rate != null ? formatPercent(overview.dkim_pass_rate) : '—'}
              accent={accentForPass(overview?.dkim_pass_rate)}
              sub="per domein → domeinenoverzicht"
              className="h-full transition hover:border-brand-300 hover:shadow-sm"
            />
          </Link>
        </div>

        {/* Domains shortcut */}
        <Card>
          <CardHeader
            title="Domeinen"
            subtitle={`${tenant?.domains_count ?? 0} gemonitorde domeinen · klik om naar lijst te gaan`}
            right={
              <Link
                to="/tenants/$slug/domains"
                params={{ slug }}
                className="inline-flex items-center gap-1 text-[11px] text-info-700 hover:text-info-600"
              >
                <Plus size={11} />
                Beheren
              </Link>
            }
          />
          <div className="px-4 py-3 text-[12px] text-slate-500">
            Volledige domeinen-tabel met DNS-snapshots en alignment-helper:{' '}
            <Link
              to="/tenants/$slug/domains"
              params={{ slug }}
              className="font-medium text-info-700 hover:text-info-600"
            >
              naar domeinenoverzicht →
            </Link>
          </div>
        </Card>

        {/* Trend + recent alerts */}
        <div className="grid grid-cols-12 gap-5">
          <Card className="col-span-8">
            <CardHeader
              title="30 dagen trend"
              subtitle={`${tenant?.name ?? slug} · gestapeld pass/quarantine/reject`}
            />
            <div className="p-4">
              <TimelineChart points={timeline} />
            </div>
          </Card>
          <Card className="col-span-4">
            <CardHeader
              title="Recente alerts"
              subtitle={`${alerts.length} relevant voor deze tenant`}
              right={
                <Link
                  to="/tenants/$slug/alerts"
                  params={{ slug }}
                  className="text-[11px] text-info-700 hover:text-info-600"
                >
                  Alles →
                </Link>
              }
            />
            <div className="max-h-[280px] divide-y divide-slate-100 overflow-auto">
              {alerts.length === 0 ? (
                <div className="px-4 py-6 text-center text-[12px] text-slate-500">
                  <Check size={20} className="mx-auto mb-1 text-green-600" />
                  Geen openstaande alerts.
                </div>
              ) : (
                alerts.map((a) => (
                  <div
                    key={a.id}
                    className={`px-4 py-2.5 ${a.acknowledged_at ? 'opacity-70' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <SevBadge sev={a.severity} />
                      <span className="font-mono text-[10px] text-slate-400">
                        {formatRelativeTime(a.triggered_at)}
                      </span>
                    </div>
                    <div className="mt-1.5 text-[12px] leading-snug text-slate-900">
                      {a.summary}
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>

        {/* Top sources */}
        <Card>
          <CardHeader
            title="Top verzendbronnen · 30 dagen"
            subtitle={`${sources.length} unieke bronnen · gefilterd op top volumes`}
            right={
              <Link
                to="/tenants/$slug/sources"
                params={{ slug }}
                className="text-[11px] text-info-700 hover:text-info-600"
              >
                Volledige analyse →
              </Link>
            }
          />
          {sources.length === 0 ? (
            <EmptyState
              title="Nog geen bronnen waargenomen"
              body="Aggregate rapporten worden vanaf de eerste publicatie van het DMARC-record verwacht. Doorgaans binnen 24 uur na publicatie."
              compact
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="border-b border-slate-200 bg-slate-50">
                  <tr>
                    <Th>IP / hostname</Th>
                    <Th>ASN</Th>
                    <Th>ESP-naam</Th>
                    <Th align="right">Volume</Th>
                    <Th>Pass-ratio</Th>
                    <Th>Klassificatie</Th>
                    <Th>rDNS</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sources.slice(0, 8).map((s) => (
                    <tr key={s.source_ip} className="hover:bg-slate-50">
                      <td className="px-3 py-2.5 font-mono text-slate-900">{s.source_ip}</td>
                      <td className="px-3 py-2.5 font-mono text-[11px] text-slate-600">
                        {s.asn ?? '—'}
                      </td>
                      <td className="px-3 py-2.5 text-slate-700">{s.esp_name ?? '—'}</td>
                      <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-900">
                        {formatNumber(s.count ?? 0)}
                      </td>
                      <td className="px-3 py-2.5">
                        <RatioBar pct={s.pass_ratio != null ? s.pass_ratio * 100 : null} />
                      </td>
                      <td className="px-3 py-2.5">
                        <ClassBadge cls={legitimacyToClass(s.esp_legitimacy)} />
                      </td>
                      <td className="px-3 py-2.5 text-slate-500">{s.reverse_dns ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Recent deliveries */}
        <Card>
          <CardHeader
            title="Recente verzendingen"
            subtitle="5 laatste rapport-PDFs"
            right={
              <Link
                to="/tenants/$slug/deliveries"
                params={{ slug }}
                className="text-[11px] text-info-700 hover:text-info-600"
              >
                Volledige verzendlog →
              </Link>
            }
          />
          {deliveries.length === 0 ? (
            <EmptyState
              title="Nog geen verzendingen"
              body="Wekelijkse rapporten starten op maandag om 07:00 in de tenant-tijdzone. Maandrapporten op de 1e van de maand."
              compact
            />
          ) : (
            <div className="divide-y divide-slate-100">
              {deliveries.map((d) => (
                <div key={d.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50">
                  <FileText
                    size={18}
                    className={d.type === 'monthly' ? 'text-info-700' : 'text-slate-400'}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className="font-mono text-[12.5px] text-slate-900">
                        {d.period ?? d.type}
                      </span>
                      {d.type === 'monthly' && (
                        <span className="rounded bg-info-50 px-1.5 py-0.5 text-[10px] font-medium text-info-700">
                          Monthly
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 text-[11px] text-slate-500">
                      {d.pages ?? '—'} pagina&apos;s ·{' '}
                      {d.size_bytes != null ? `${(d.size_bytes / 1024).toFixed(0)} kB` : '—'} ·{' '}
                      {d.sent_at ? formatRelativeTime(d.sent_at) : 'nog niet verstuurd'}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {d.smtp_status === 'sent' ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-green-700">
                        <Check size={11} />
                        verzonden
                      </span>
                    ) : d.smtp_status === 'bounced' || d.smtp_status === 'failed' ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-red-700">
                        <X size={11} />
                        {d.smtp_status}
                      </span>
                    ) : (
                      <span className="text-[11px] text-slate-500">{d.smtp_status ?? '—'}</span>
                    )}
                    <a
                      href={`/api/v1/deliveries/${d.id}/pdf`}
                      className="text-slate-400 hover:text-slate-900"
                      aria-label="Download PDF"
                    >
                      <Download size={14} />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {isAdmin && (
          <Card className="border-red-200">
            <CardHeader title="Danger zone" subtitle="Onomkeerbare acties (admin-only)" />
            <div className="flex items-center justify-between gap-4 px-4 py-3">
              <p className="text-[12px] text-slate-600">
                Verwijder deze tenant en alle bijbehorende rapporten, domeinen, alerts en
                PDF-archieven definitief. De audit-log blijft intact als bewijs van de actie.
              </p>
              <button
                type="button"
                onClick={() => setPurgeOpen(true)}
                className="whitespace-nowrap rounded border border-red-300 px-3 py-1.5 text-[12px] font-medium text-red-700 hover:bg-red-50"
              >
                Tenant onomkeerbaar verwijderen
              </button>
            </div>
          </Card>
        )}
      </QueryState>

      <TenantPurgeModal
        slug={slug}
        open={purgeOpen}
        onClose={() => setPurgeOpen(false)}
        onPurged={() => navigate({ to: '/tenants' })}
      />

      <SendTestReportModal open={reportOpen} onClose={() => setReportOpen(false)} slug={slug} />
    </div>
  );
}

// ----- helpers -------------------------------------------------------------

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      className={`px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 text-${align}`}
    >
      {children}
    </th>
  );
}

function StatusBadge({ status }: { status?: string }) {
  // Tenant lifecycle status. Record<string,…> + noUncheckedIndexedAccess forces
  // the fallback below, so any unexpected/empty value renders a neutral badge
  // instead of crashing on `undefined.bg`.
  const map: Record<string, { label: string; bg: string; text: string; dot: string }> = {
    active: { label: 'Actief', bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-600' },
    suspended: { label: 'Opgeschort', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-600' },
    archived: { label: 'Gearchiveerd', bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-400' },
  };
  const m = map[status ?? ''] ?? {
    label: status ?? 'Onbekend',
    bg: 'bg-slate-100',
    text: 'text-slate-700',
    dot: 'bg-slate-400',
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs ${m.bg} ${m.text} rounded font-medium`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />
      {m.label}
    </span>
  );
}

function ClassBadge({ cls }: { cls?: string }) {
  const map: Record<string, { label: string; bg: string; text: string; dot: string }> = {
    legitimate: {
      label: 'Legitimate',
      bg: 'bg-green-50',
      text: 'text-green-700',
      dot: 'bg-green-600',
    },
    misalignment: {
      label: 'Misalignment',
      bg: 'bg-amber-50',
      text: 'text-amber-700',
      dot: 'bg-amber-600',
    },
    unknown: { label: 'Unknown', bg: 'bg-slate-100', text: 'text-slate-700', dot: 'bg-slate-500' },
    suspect: { label: 'Suspect', bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-600' },
  };
  const m = map[cls ?? ''] ?? {
    label: cls ?? 'Unknown',
    bg: 'bg-slate-100',
    text: 'text-slate-700',
    dot: 'bg-slate-500',
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-[11px] font-medium ${m.bg} ${m.text}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />
      {m.label}
    </span>
  );
}

function legitimacyToClass(v?: string | null): string {
  if (v === 'known_good') return 'legitimate';
  if (v === 'known_bad') return 'suspect';
  return 'unknown';
}

function accentForPass(rate?: number): string | undefined {
  if (rate == null) return undefined;
  if (rate >= 0.95) return 'text-green-700';
  if (rate >= 0.9) return 'text-amber-700';
  return 'text-red-700';
}

function toneOf(v?: number): 'up' | 'down' | 'flat' {
  if (v == null) return 'flat';
  return v >= 0 ? 'up' : 'down';
}

function fmtDelta(v: number | undefined, suffix: string, kind: 'pct' | 'pp'): string | undefined {
  if (v == null) return undefined;
  const sign = v >= 0 ? '+' : '';
  const formatted = kind === 'pp' ? v.toFixed(1) : v.toFixed(1);
  return `${sign}${formatted.replace('.', ',')}${suffix}`;
}

function TimelineChart({ points }: { points: TimelinePoint[] }) {
  if (points.length === 0) {
    return (
      <div className="flex h-[200px] items-center justify-center text-[12px] text-slate-500">
        Nog geen data voor deze periode.
      </div>
    );
  }
  const data = points.map((p) => ({
    day: p.ts ? formatDate(p.ts, 'd MMM') : '',
    pass: p.pass,
    quarantine: p.quarantine,
    reject: p.reject,
  }));
  return (
    <ResponsiveContainer width="100%" height={200}>
      <ComposedChart data={data} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
        <CartesianGrid stroke="#e2e8f0" strokeDasharray="0" vertical={false} />
        <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#64748b' }} />
        <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} />
        <Tooltip
          formatter={(v: number) => formatNumber(v)}
          labelStyle={{ fontSize: 11 }}
          contentStyle={{ fontSize: 11, borderRadius: 4 }}
        />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar dataKey="pass" stackId="x" fill="#16a34a" name="Pass" />
        <Bar dataKey="quarantine" stackId="x" fill="#d97706" name="Quarantine" />
        <Bar dataKey="reject" stackId="x" fill="#dc2626" name="Reject" />
      </ComposedChart>
    </ResponsiveContainer>
  );
}
